import type { ResourceProps } from '@refinedev/core';

// All admin resources live under /admin/* so the bare root path can host the
// track-list landing page. Refine's `useMenu` reads these paths to render the
// sidebar; keep them in sync with App.tsx's <Route> tree.
export const resources: ResourceProps[] = [
  {
    name: 'users',
    list: '/admin/users',
    show: '/admin/users/show/:id',
    edit: '/admin/users/edit/:id',
    meta: { label: 'Users', canDelete: false },
  },
  {
    name: 'api-keys',
    list: '/admin/api-keys',
    create: '/admin/api-keys/create',
    meta: { label: 'API Keys', canDelete: true },
  },
  {
    // UI renamed Relations → Permissions (M27 ADM-10; UI ONLY — the hub
    // `/relations*` REST API + `relations*` client methods are unchanged, §4
    // D-11). Only the resource name/label/route move.
    name: 'permissions',
    list: '/admin/permissions',
    create: '/admin/permissions/create',
    meta: { label: 'Permissions', canDelete: true },
  },
  // M27 — named-model registry + per-purpose assignment (ADM-01/03; HUB-082).
  {
    name: 'models',
    list: '/admin/models',
    create: '/admin/models/create',
    edit: '/admin/models/edit/:id',
    meta: { label: 'Models', canDelete: true },
  },
  {
    name: 'assignments',
    list: '/admin/assignments',
    meta: { label: 'Model Assignment', canDelete: false },
  },
  // M27 — DB-backed login-SSO + Drive-connector OAuth config (ADM-07/18; HUB-083).
  {
    name: 'identity-providers',
    list: '/admin/identity-providers',
    meta: { label: 'Identity Providers', canDelete: false },
  },
  // M27 — DB-backed web-research provider config (ADM-15; HUB-084).
  {
    name: 'web-research',
    list: '/admin/web-research',
    meta: { label: 'Web Research', canDelete: false },
  },
  // HUB-107 / COP-152 — MCP server registry (admin): connect type, secrets,
  // tool allowlist.
  {
    name: 'mcp-servers',
    list: '/admin/mcp-servers',
    meta: { label: 'MCP Servers', canDelete: true },
  },
  // M24 — DB connectors (DBC-01/03/04). Show is auth-gated only (NOT under
  // RequireAdmin) so editor-granted non-admins can deep-link to the schema
  // editor; see the route comment in App.tsx.
  {
    name: 'db-connectors',
    list: '/admin/db-connectors',
    create: '/admin/db-connectors/create',
    show: '/admin/db-connectors/:id',
    meta: { label: 'DB Connectors', canDelete: true },
  },
  // M28 — cross-track document master/detail + hybrid search (ADM-12/13;
  // COP-117 D-14). ONE component mounted here for admins; task-06 mounts the
  // same source at /me/documents. Show is `read`-gated per document by the hub.
  {
    name: 'documents',
    list: '/admin/documents',
    show: '/admin/documents/show/:id',
    meta: { label: 'Documents', canDelete: false },
  },
  {
    name: 'tracks',
    list: '/admin/tracks',
    show: '/admin/tracks/show/:id',
    create: '/admin/tracks/create',
    edit: '/admin/tracks/edit/:id',
    meta: { label: 'Tracks', canDelete: true },
  },
  {
    name: 'prompts',
    list: '/admin/prompts',
    show: '/admin/prompts/show/:id',
    create: '/admin/prompts/create',
    edit: '/admin/prompts/edit/:id',
    meta: { label: 'Prompts', canDelete: true },
  },
  // APPR-07 / D-16 — admin queue surfaces pending + decided approval rows.
  // Audit append-only (canDelete: false). Click-through from List → Show
  // forwards `?userId=` so the Show page can scope the adminGetApproval
  // call under withUserRole(targetUserId) per HUB-024 §6.
  {
    name: 'approvals',
    list: '/admin/approvals',
    show: '/admin/approvals/show/:id',
    meta: { label: 'Approvals', canDelete: false },
  },
  // S08 — owner scheduled actions tab. This is a user-owned surface backed by
  // `/schedules` and `/notifications`, not an admin cross-owner browser.
  {
    name: 'schedules',
    list: '/admin/schedules',
    meta: { label: 'Scheduled actions', canDelete: true },
  },
  // M33 — bounded adoption surface over the audit log (ATRU-06; COP-134 D4).
  // Read-only singleton page: no create/edit/show, no delete.
  {
    name: 'usage',
    list: '/admin/usage',
    meta: { label: 'Usage', canDelete: false },
  },
  {
    name: 'settings',
    // Singleton "resource" — no create/edit/show, just a single page where
    // an admin uploads the company logo for the shared header.
    list: '/admin/settings',
    meta: { label: 'Settings', canDelete: false },
  },
];
